import { Component, OnChanges, OnInit, OnDestroy, SimpleChanges, TemplateRef, ChangeDetectionStrategy, inject, input, output, viewChild, signal } from '@angular/core';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import {environment} from '@environments/environment';
import SnoballPageDesigner from '../../../assets/snoball_page_designer/js/script.js';
import {loadJQuery} from '@app/_helpers/load-jquery';
import {isChunkLoadError, reloadIntoCurrentBuild} from '@app/_helpers/chunk-load-recovery';
import {AppVersionService} from '@app/services/app-version.service';
import { IframeModalShellComponent } from '../iframe-modal-shell/iframe-modal-shell.component';
import { sameColor, toHexString } from '../color-panel/color-format';
import { ColorPanelRef, ColorPanelService } from '../color-panel/color-panel.service';
import { EMPTY_COLOR_STATE, setStateColor, stateColor } from '../color-panel/color-panel-state';

interface ImageProcessLimits {maxSide: number; maxPadding: number; fits: string[]}

/** The designer's names for the app's viewport sizes. */
const DESIGNER_VIEWPORTS: Record<string, string> = {desktop: 'desktop', tablet: 'tablet_portrait', mobile: 'phone'};

/** The longest the host's first calls wait for the designer's frame to load. */
const FRAME_LOAD_WAIT_MS = 10000;

/**
 * What the designer needs on the page before it is built: jQuery, which its script reads as the
 * global `$`, and the block settings plugin (height, margins, padding, background) on it. No
 * other page loads them; a designer opened again in the same tab finds both still there.
 */
function loadDesignerScripts(): Promise<unknown> {
  return loadJQuery().then((jQuery) => 'editorSettings' in jQuery.fn
    ? undefined
    : import('../../../assets/snoball_page_designer/editor-settings/editor-settings.js'));
}

/** Resolves once `frame` has loaded its document, stylesheets included, or after a while at most. */
function frameLoaded(frame: HTMLIFrameElement | undefined): Promise<void> {
  return new Promise(resolve => {
    if (!frame || frame.contentDocument?.readyState === 'complete') {
      resolve();
      return;
    }
    frame.addEventListener('load', () => resolve(), {once: true});
    setTimeout(resolve, FRAME_LOAD_WAIT_MS);
  });
}

@Component({
  selector: 'app-page-designer',
  templateUrl: './page-designer.component.html',
  styleUrls: [
    './page-designer.component.scss',
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IframeModalShellComponent]
})
export class PageDesignerComponent implements OnChanges, OnInit, OnDestroy {
  private _modalService = inject(NgbModal);
  private sanitizer = inject(DomSanitizer);
  private appVersionService = inject(AppVersionService);
  private colorPanel = inject(ColorPanelService);

  readonly OnContentChanged = output<void>();
  readonly variablePickerModal = viewChild<TemplateRef<any>>('variablePickerModal');
  /** desktop, tablet or mobile. */
  readonly ViewportSize = input<string>(undefined);
  readonly token = input<string>(undefined);
  readonly Variables = input<object>(undefined);
  readonly ImageAutoCropEnabled = input<boolean>(undefined);
  // What /api/v1/image-process draws, sent by the server with the template, for the crop dialog.
  readonly ImageProcessLimits = input<ImageProcessLimits | null>(null);

  readonly variablePickerUrl = signal<SafeResourceUrl | null>(null);
  /** Set when the designer could not be loaded and the page was not reloaded for it. */
  readonly loadFailed = signal(false);

  private snoballPageDesigner;
  /** Calls that came before the designer took calls, made in order once it does; null from then. */
  private pendingCalls: Array<() => void> | null = [];
  /** Settles once the designer takes calls, or it is not going to. */
  private designerReady: Promise<void>;
  private destroyed = false;
  private _pendingVariableContext: string;
  private _messageListener: (event: MessageEvent) => void;

  /** A changed binding goes to the designer once it takes calls; it is built with the current ones. */
  ngOnChanges(changes: SimpleChanges): void {
    if (!this.snoballPageDesigner) {
      // A viewport picked while the designer loads is the one binding it is not built with.
      if (changes.ViewportSize && !changes.ViewportSize.firstChange) {
        this.whenBuilt(() => this.changeViewportSize());
      }
      return;
    }
    if (changes.ViewportSize) {
      this.whenBuilt(() => this.changeViewportSize());
    }
    if (changes.Variables) {
      this.whenBuilt(() => this.snoballPageDesigner.setVariables(this.Variables()));
    }
    if (changes.ImageAutoCropEnabled) {
      this.whenBuilt(() => this.snoballPageDesigner.setImageAutoCropEnabled(this.ImageAutoCropEnabled()));
    }
    if (changes.ImageProcessLimits) {
      this.whenBuilt(() => this.snoballPageDesigner.setImageProcessLimits(this.ImageProcessLimits()));
    }
  }

  ngOnInit(): void {
    this._messageListener = this.receiveMessage.bind(this);
    window.addEventListener('message', this._messageListener, false);

    this.designerReady = loadDesignerScripts()
      .then(() => this.buildDesigner())
      .then(() => this.takeCalls(), (error) => {
        const chunkGone = isChunkLoadError(error);
        // After a deploy this build's chunks are gone; nothing is edited yet, so load the page anew.
        if (chunkGone && reloadIntoCurrentBuild(window.location.href)) {
          return;
        }
        // Otherwise the editor says so instead of staying blank, and a save is refused.
        if (chunkGone) {
          this.appVersionService.markStale();
        }
        console.error(error);
        this.pendingCalls = [];
        this.loadFailed.set(true);
      });
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pendingCalls = [];
    if (this._messageListener) {
      window.removeEventListener('message', this._messageListener, false);
    }
    this.snoballPageDesigner?.destroy();
    this.snoballPageDesigner = null;
  }

  receiveMessage(event: MessageEvent): void {
    if (event.data && event.data.type === 'variable' && event.data.target === 'froala_variable_picker') {
      this.handleVariableSelected(event.data.data);
    }
  }

  openVariablePicker(context: string): void {
    this._pendingVariableContext = context;
    const pagePath = window.location.pathname + window.location.hash;
    const fieldName = 'froala_editor';
    const fieldTypeMap = { text: 'text', linkUrl: 'url', linkText: 'text', image: 'image', video: 'video' };
    const fieldType = fieldTypeMap[context] || 'text';
    const baseUrl = environment.frontUrl.replace('/#', '').replace(/\/+$/, '');
    const theme = document.body.classList.contains('dark-theme') ? 'dark' : 'light';
    // The token alone: the picker signs its usage log with it.
    const appContext = this.token() ? `&appcontext=${encodeURIComponent(JSON.stringify({ token: this.token() }))}` : '';
    const url = `${baseUrl}/b/variable/template?type=variable&target=froala_variable_picker&field_name=${encodeURIComponent(fieldName)}&field_type=${encodeURIComponent(fieldType)}&format=html&page_path=${encodeURIComponent(pagePath)}&theme=${theme}${appContext}`;

    setTimeout(() => {
      this.variablePickerUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(url));
    });

    const modalRef = this._modalService.open(this.variablePickerModal(), {
      centered: false,
      size: 'xl',
      keyboard: true,
      windowClass: 'snoball-iframe-modal-window'
    });
    modalRef.result.then(
      () => {
        this.variablePickerUrl.set(null);
      },
      () => {
        this.variablePickerUrl.set(null);
      }
    );
  }

  /**
   * The block background colour: the app's colour panel, kept inside the designer's toolbar so the toolbar stays
   * open under it. A chosen colour that differs from the block's goes to the block as `#rrggbb`.
   */
  openColorPicker(origin: HTMLElement, color: string, change: (hex: string) => void): ColorPanelRef {
    return this.colorPanel.open(origin, {
      state: setStateColor(EMPTY_COLOR_STATE, color || null),
      inline: true,
      closed: ({reason, state, initial}) => {
        const chosen = stateColor(state);
        if (reason === 'choose' && chosen && !sameColor(chosen, stateColor(initial))) {
          change(toHexString(chosen));
        }
      },
    });
  }

  handleVariableSelected(variable: any): void {
    if (this.snoballPageDesigner && variable && variable.key) {
      this.snoballPageDesigner.handleVariableSelected(variable);
    }
    this._modalService.dismissAll();
  }

  setHtml(val, callBack): void {
    this.whenBuilt(() => this.snoballPageDesigner.setHtml(val, callBack));
  }

  hideToolbars() {
    return this.snoballPageDesigner?.hideBlockToolbars();
  }

  /** The design to save, once the designer has it; refused when the designer never loaded. */
  async getHtmlParts() {
    await this.designerReady;
    if (!this.snoballPageDesigner || this.pendingCalls) {
      throw new Error('The page designer is not loaded');
    }
    return await this.snoballPageDesigner.getHtmlParts();
  }

  appendBlock(val, type): void {
    this.whenBuilt(() => this.snoballPageDesigner.appendBlock(val, type));
  }

  customCSSStyles(value): void {
    this.whenBuilt(() => this.snoballPageDesigner.customCSSStyles(value));
  }

  changeStyle(selector, name, value, important = false): void {
    this.whenBuilt(() => {
      if (name === 'fontFamily') {
        this.snoballPageDesigner.loadGoogleFont(value);
      }
      this.snoballPageDesigner.changeStyle(selector, name, value, important);
    });
  }

  /** Sizes the designer's frame, which the designer creates. */
  setIframeSizes(width?, height?, aspect_ratio?) {
    this.whenBuilt(() => {
      const editorEl = document.querySelector('#froala-pages');
      if (editorEl) {
        const el = editorEl as HTMLElement;
        if (width !== undefined) {
          el.style.width = width + 'px';
        }
        if (height !== undefined) {
          el.style.height = height + 'px';
        }
        if (aspect_ratio !== undefined) {
          el.style['aspect-ratio'] = `${aspect_ratio}`;
        }
      }
    });
  }

  setEditorHeight(height) {
    if (height) {
      this.whenBuilt(() => this.snoballPageDesigner.setEditorHeight(height, true));
    }
  }

  destroy() {
    if (this.snoballPageDesigner) {
      this.snoballPageDesigner.destroy();
    }
  }

  disableEditor() {
    this.whenBuilt(() => this.snoballPageDesigner.disableEditor());
  }

  enableEditor() {
    this.whenBuilt(() => this.snoballPageDesigner.enableEditor());
  }

  /** Builds the designer, and resolves when it can take the calls waiting for it. */
  private buildDesigner(): Promise<void> {
    if (this.destroyed) {
      return Promise.resolve();
    }
    this.snoballPageDesigner = new SnoballPageDesigner('froala-editor', {
      froalaLicenseKey: environment.froalaLicenseKey,
      variables: this.Variables(),
      authToken: this.token(),
      imageUploadUrl: environment.apiUrl + '/api/v1/file',
      cropImageUploadUrl: environment.apiUrl + '/api/v1/image-process',
      imageAutoCropEnabled: this.ImageAutoCropEnabled(),
      imageProcessLimits: this.ImageProcessLimits(),
      styles: [
        environment.apiUrl + '/public/assets/templates/v1/blocks/custom.css?v1=' + (new Date()).getTime() // V1 For firefox to load css
      ],
      customJs: [
        environment.apiUrl + '/public/assets/js/font-size-for-editor.js?v1=' + (new Date()).getTime() // V1 For firefox to load
      ],
      fontFamily: {},
    });
    this.snoballPageDesigner.onContentChanged = () => this.OnContentChanged.emit();
    this.snoballPageDesigner.onVariablePickerRequested = (context: string) => this.openVariablePicker(context);
    this.snoballPageDesigner.onColorPickerRequested =
      (origin: HTMLElement, color: string, change: (hex: string) => void) => this.openColorPicker(origin, color, change);
    // The designer writes its frame as it is built. A design set before the frame's stylesheets
    // are in is laid out unstyled, too tall, and the frame never shrinks back; so calls already
    // waiting (the template came first) wait for the frame, as the template did when it came after.
    return frameLoaded(this.pendingCalls.length ? this.snoballPageDesigner.froala?.iframe : undefined);
  }

  /** Makes the calls that waited for the designer, in order; calls go to it directly from now on. */
  private takeCalls(): void {
    if (this.destroyed) {
      return;
    }
    const pendingCalls = this.pendingCalls;
    this.pendingCalls = null;
    pendingCalls.forEach(call => call());
  }

  /** Makes `call` now if the designer takes calls, or once it does. */
  private whenBuilt(call: () => void): void {
    if (this.pendingCalls) {
      this.pendingCalls.push(call);
    } else {
      call();
    }
  }

  private changeViewportSize(): void {
    const size = DESIGNER_VIEWPORTS[this.ViewportSize()] || this.ViewportSize();
    if (size) {
      this.snoballPageDesigner.changeViewportSize(size);
    }
  }
}
